import React, { useEffect, useMemo, useState } from "react";
import { API_URL, authFetch } from "../../utils/auth";
import ToolSidebar from "../../utils/ToolSideBar";
import "../../../css/superuser/StaffDirectoryPage.css";
import "../../../css/staff/InternalWorkspace.css";

function SummaryCard({ label, value }) {
  return <article className="approval-summary-card"><span>{label}</span><strong>{value || 0}</strong></article>;
}

function todayValue() {
  return new Date().toISOString().slice(0, 10);
}

function compensationDraft(employee) {
  const compensation = employee.compensation || {};
  const firstPosition = employee.compensation_options?.positions?.[0]?.value || "partner";
  const firstPayType = employee.compensation_options?.pay_types?.[0]?.value || "commission";
  return {
    position: compensation.position || (employee.role === "partner" ? "partner" : firstPosition),
    pay_type: compensation.pay_type || (employee.role === "partner" ? "commission" : firstPayType),
    pay_value: compensation.pay_value || "",
    effective_date: todayValue(),
  };
}

function EmployeeCard({ employee, canManageCompensation, onSaveCompensation, savingId }) {
  const isSales = employee.role === "partner";
  const [draft, setDraft] = useState(() => compensationDraft(employee));
  const current = employee.compensation;
  const history = employee.compensation_history || [];
  const options = employee.compensation_options || { positions: [], pay_types: [] };

  useEffect(() => {
    setDraft(compensationDraft(employee));
  }, [employee]);

  function updateDraft(field, value) {
    setDraft((currentDraft) => ({ ...currentDraft, [field]: value }));
  }

  function submitCompensation(event) {
    event.preventDefault();
    onSaveCompensation(employee, draft);
  }

  return (
    <article className="employee-card">
      <div className="employee-card-head">
        <div><h2>{employee.name}</h2><span>{employee.role_label}</span></div>
        <strong className={employee.active ? "is-active" : "is-inactive"}>{employee.status}</strong>
      </div>
      <div className="approval-meta-grid">
        <div><span>Email</span><strong>{employee.email || "Not provided"}</strong></div>
        {employee.developer_discipline_label && <div><span>Discipline</span><strong>{employee.developer_discipline_label}</strong></div>}
        {isSales ? <div><span>Active Leads</span><strong>{employee.active_leads}</strong></div> : <div><span>Team</span><strong>{employee.team || "Unassigned"}</strong></div>}
        {isSales ? <div><span>Commissions</span><strong>{employee.commission_count}</strong></div> : <div><span>Project</span><strong>{employee.current_project || "No active project"}</strong></div>}
        {canManageCompensation && <div><span>Compensation</span><strong>{current?.display_value || "Not finalized"}</strong></div>}
      </div>
      {canManageCompensation && (
        <form className="employee-compensation-form" onSubmit={submitCompensation}>
          <select aria-label={`Position for ${employee.name}`} value={draft.position} onChange={(event) => updateDraft("position", event.target.value)} required>
            {options.positions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select aria-label={`Pay type for ${employee.name}`} value={draft.pay_type} onChange={(event) => updateDraft("pay_type", event.target.value)} required>
            {options.pay_types.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <input aria-label={`Pay value for ${employee.name}`} type="number" min="0" step="0.01" value={draft.pay_value} onChange={(event) => updateDraft("pay_value", event.target.value)} required />
          <input aria-label={`Effective date for ${employee.name}`} type="date" value={draft.effective_date} onChange={(event) => updateDraft("effective_date", event.target.value)} required />
          <button type="submit" disabled={savingId === employee.user_id}>{savingId === employee.user_id ? "Saving..." : "Save Compensation"}</button>
          {history.length > 0 && <small>History: {history.map((item) => `${item.effective_date}: ${item.display_value}`).join(" | ")}</small>}
        </form>
      )}
    </article>
  );
}

export default function EmployeesPage() {
  const params = new URLSearchParams(window.location.search);
  const [group, setGroup] = useState(params.get("group") || params.get("role") || "");
  const [discipline, setDiscipline] = useState(params.get("developer_discipline") || "");
  const [search, setSearch] = useState("");
  const [payload, setPayload] = useState({ results: [], summary: {}, filters: [], developer_disciplines: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [savingId, setSavingId] = useState(null);
  const canManageCompensation = localStorage.getItem("is_superuser") === "true";

  const query = useMemo(() => {
    const next = new URLSearchParams();
    if (group) next.set(group === "partner" ? "role" : "group", group);
    if (discipline) next.set("developer_discipline", discipline);
    if (search) next.set("search", search);
    return next.toString();
  }, [group, discipline, search]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const response = await authFetch(`${API_URL}/ops/employees/${query ? `?${query}` : ""}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || "Unable to load employees.");
        setPayload(data);
        setError("");
      } catch (err) {
        setError(err.message || "Unable to load employees.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [query]);

  async function saveCompensation(employee, draft) {
    setSavingId(employee.user_id);
    setError("");
    setStatus("");
    try {
      const response = await authFetch(`${API_URL}/ops/employees/${employee.user_id}/compensation/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to save compensation.");
      if (data.employee) {
        setPayload((current) => ({
          ...current,
          results: current.results.map((item) => item.user_id === employee.user_id ? data.employee : item),
        }));
      }
      setStatus(`${employee.name} compensation saved.`);
    } catch (err) {
      setError(err.message || "Unable to save compensation.");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="staff-shell">
      <ToolSidebar active="employees" />
      <main className="staff-page internal-page employees-page">
        <header className="staff-header internal-hero">
          <span>{payload.eyebrow || "SCD PEOPLE"}</span>
          <h1>{payload.heading || "Employees"}</h1>
          <p>{payload.supporting_text || "View SCD staff, roles, teams, and active assignments."}</p>
        </header>

        {status && <p className="staff-empty inline">{status}</p>}

        <section className="approval-summary-grid">
          <SummaryCard label="Active Employees" value={payload.summary?.active_employees} />
          <SummaryCard label="Sales" value={payload.summary?.sales} />
          <SummaryCard label="Delivery" value={payload.summary?.delivery} />
          <SummaryCard label="Unassigned" value={payload.summary?.unassigned} />
        </section>

        <section className="staff-card workboard-toolbar">
          <input aria-label="Search employees" placeholder="Search employees" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select aria-label="Employee role" value={group} onChange={(e) => { setGroup(e.target.value); setDiscipline(""); }}>
            {(payload.filters || [{ value: "", label: "All Employees" }]).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select aria-label="Developer discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} disabled={group !== "developers"}>
            <option value="">All developer disciplines</option>
            {(payload.developer_disciplines || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </section>

        <section className="staff-card employee-list-card">
          {loading ? <p className="staff-empty inline">Loading employees...</p> : error ? <p className="staff-empty inline team-error">{error}</p> : payload.results.length === 0 ? <p className="staff-empty inline">No employees found.</p> : <div className="employee-card-grid">{payload.results.map((employee) => <EmployeeCard key={employee.id} employee={employee} canManageCompensation={canManageCompensation && Boolean(employee.compensation_options)} onSaveCompensation={saveCompensation} savingId={savingId} />)}</div>}
        </section>
      </main>
    </div>
  );
}